/**
 * <s-product-gallery> — single-photo product gallery with arrows and thumbnails.
 *
 * Required markup:
 *   [data-product-gallery]   — horizontal scroll-snap track; each direct child is one slide.
 *   [data-gallery-prev/next] — optional arrow buttons.
 *   [data-gallery-thumb="i"] — optional thumbnail buttons; the active one gets aria-current="true".
 */
class SProductGallery extends HTMLElement {
  #ac;
  #track;
  #frame = 0;

  connectedCallback() {
    this.#track = this.querySelector("[data-product-gallery]");
    if (!this.#track) return;
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.querySelector("[data-gallery-prev]")?.addEventListener("click", () => this.#go(this.#index() - 1), opts);
    this.querySelector("[data-gallery-next]")?.addEventListener("click", () => this.#go(this.#index() + 1), opts);
    this.addEventListener(
      "click",
      (event) => {
        const thumb = event.target.closest("[data-gallery-thumb]");
        if (thumb) this.#go(Number(thumb.dataset.galleryThumb));
      },
      opts,
    );
    this.#track.addEventListener(
      "scroll",
      () => {
        cancelAnimationFrame(this.#frame);
        this.#frame = requestAnimationFrame(() => this.#sync());
      },
      { ...opts, passive: true },
    );
    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  #slides() {
    return Array.from(this.#track.children);
  }

  #index() {
    const width = this.#track.clientWidth || 1;
    return Math.round(Math.abs(this.#track.scrollLeft) / width);
  }

  #go(index) {
    const slides = this.#slides();
    if (!slides.length) return;
    const target = Math.max(0, Math.min(slides.length - 1, index));
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const dir = getComputedStyle(this.#track).direction === "rtl" ? -1 : 1;
    this.#track.scrollTo({ left: dir * target * this.#track.clientWidth, behavior: reduce ? "auto" : "smooth" });
  }

  #sync() {
    const index = this.#index();
    const count = this.#slides().length;
    this.querySelectorAll("[data-gallery-thumb]").forEach((thumb) => {
      const active = Number(thumb.dataset.galleryThumb) === index;
      if (active) {
        thumb.setAttribute("aria-current", "true");
        this.#reveal(thumb);
      } else {
        thumb.removeAttribute("aria-current");
      }
    });
    const prev = this.querySelector("[data-gallery-prev]");
    const next = this.querySelector("[data-gallery-next]");
    if (prev) prev.disabled = index <= 0;
    if (next) next.disabled = index >= count - 1;
  }

  // Keep the active thumbnail visible inside its own scrolling list without scrolling the page.
  #reveal(thumb) {
    const list = thumb.closest("ul");
    if (!list) return;
    const item = thumb.parentElement;
    if (list.scrollHeight > list.clientHeight) {
      if (item.offsetTop < list.scrollTop) list.scrollTop = item.offsetTop;
      else if (item.offsetTop + item.offsetHeight > list.scrollTop + list.clientHeight) list.scrollTop = item.offsetTop + item.offsetHeight - list.clientHeight;
    }
    if (list.scrollWidth > list.clientWidth) {
      if (item.offsetLeft < list.scrollLeft) list.scrollLeft = item.offsetLeft;
      else if (item.offsetLeft + item.offsetWidth > list.scrollLeft + list.clientWidth) list.scrollLeft = item.offsetLeft + item.offsetWidth - list.clientWidth;
    }
  }
}


if (!customElements.get("s-product-gallery")) {
  customElements.define("s-product-gallery", SProductGallery);
}
